import { useCallback } from 'react';
import { useDisclosure } from '@ds';
import { DayCell } from '@lp/features/Scheduler/components';
import { SchedulerDayColumn, ShiftSiblingData } from '@lp/features/Scheduler/types';
import { NAShiftModal, SShiftCell, SShiftEmptyCell } from '@lp/features/Scheduler/SchedulerSurgeons/components';
import { ShiftUnavailabilitiesFormType } from '@lp/features/Scheduler/SchedulerSurgeons/components/NAShiftModal/types';
import { SResource, SShift, SShiftSelectPayload, SShiftType } from '@/api/apis/resources/schedule';

type SDayCellProps = {
  column: SchedulerDayColumn;
  shiftData?: SShift;
  resource: SResource;
  siblingData?: ShiftSiblingData;
  isEditable: boolean;
  canReset: boolean;
  highlight?: boolean;
  canUnblock?: boolean;
  onShiftChange: (payload?: SShiftSelectPayload) => void;
  onUnblock?: () => void;
};

export const SDayCell = ({
  column,
  shiftData,
  resource,
  siblingData,
  isEditable,
  canReset,
  highlight,
  canUnblock,
  onShiftChange,
  onUnblock,
}: SDayCellProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const readonly = !isEditable || !column.isCurrentMonth;

  const handleShiftChange = useCallback(
    (shiftType?: SShiftType) => {
      if (shiftType === SShiftType.Unavailable) onOpen();
      else onShiftChange(shiftType ? { shiftType } : undefined);
    },
    [onShiftChange, onOpen],
  );

  const onNASubmit = (formData: ShiftUnavailabilitiesFormType) => {
    const { fullDay, unavailabilities } = formData;
    if (fullDay) onShiftChange({ shiftType: SShiftType.Unavailable });
    else onShiftChange({ shiftType: SShiftType.OnCall, unavailabilities });
    onClose();
  };

  return (
    <>
      <DayCell
        active={column.isSelectedDay}
        highlight={highlight}
        hasPrev={siblingData?.hasPrev}
        hasNext={siblingData?.hasNext}
      >
        {shiftData ? (
          <SShiftCell
            shiftData={shiftData}
            siblingData={siblingData}
            lighten={!column.isCurrentMonth}
            readonly={readonly}
            canReset={canReset}
            canUnblock={canUnblock}
            onShiftChange={handleShiftChange}
            onUnblock={onUnblock}
          />
        ) : (
          !readonly && <SShiftEmptyCell onShiftChange={handleShiftChange} />
        )}
      </DayCell>
      {isOpen && (
        <NAShiftModal
          day={column.key}
          resource={resource}
          shiftData={shiftData}
          onClose={onClose}
          onConfirm={onNASubmit}
        />
      )}
    </>
  );
};
